import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "cfm_cart";

// Cart items: { product_id, variant_id, qty, name_gr, name_en, variant_label, price, image, max }
export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const variantMax = (variant) =>
    variant?.availability === "in_stock" ? Math.max(1, Number(variant?.stock ?? 1)) : 20;

  const add = useCallback((product, variant, qty = 1, imageOverride = null) => {
    const max = variantMax(variant);
    setItems((prev) => {
      const i = prev.findIndex((x) => x.product_id === product.id && x.variant_id === variant.id);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], max, qty: Math.min(max, next[i].qty + qty) };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          variant_id: variant.id,
          qty: Math.min(max, qty),
          max,
          name_gr: product.name_gr,
          name_en: product.name_en,
          variant_label: variant.label || "",
          price: variant.sale_price != null ? Number(variant.sale_price) : variant.price != null ? Number(variant.price) : Number(product.price),
          image: imageOverride ?? product.images?.[0] ?? null,
        },
      ];
    });
  }, []);

  const setQty = useCallback((product_id, variant_id, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((x) => !(x.product_id === product_id && x.variant_id === variant_id))
        : prev.map((x) => (x.product_id === product_id && x.variant_id === variant_id ? { ...x, qty: Math.min(x.max ?? 20, qty) } : x))
    );
  }, []);

  const remove = useCallback((product_id, variant_id) => {
    setItems((prev) => prev.filter((x) => !(x.product_id === product_id && x.variant_id === variant_id)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, x) => s + x.qty, 0), [items]);
  const subtotal = useMemo(() => Math.round(items.reduce((s, x) => s + x.price * x.qty, 0) * 100) / 100, [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
